import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import { Box, Flex, Image, Text } from 'Components/Base';
import SvgIcon, { ICON_LIST } from 'Components/Base/Icon/SvgIcon';
import ProductChip from 'Components/ProductChip';
import {
  clamp,
  formatDuration,
  getColorBySubjectName,
  getFallbackImage,
  PRODUCT_TYPE,
} from 'Util/myPath';

import { ENCOURAGEMENT_TYPE, mapCompletedMilestonesToEncouragementText, MILESTONES } from './utils';
import ProgressBar from './ProgressBar';

/* ----------------- styled components ----------------- */

const CardWrapper = styled(Flex)`
  flex-direction: column;
  gap: 18px;
  padding: ${({ theme }) => theme.space[2]}px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.myPath.gray[200]};
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  border-left: 8px solid ${({ theme, $subjectName }) => getColorBySubjectName(theme, $subjectName)};
`;

const HeaderRow = styled(Flex)`
  justify-content: space-between;
  row-gap: ${({ theme }) => theme.space[2]}px;
`;

const LeftGroup = styled(Flex)`
  column-gap: ${({ theme }) => theme.space[2]}px;
  min-width: 0;
  overflow: hidden;
`;

const Thumbnail = styled(Box)`
  width: 113px;
  height: 64px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.myPath.gray[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const TitleBlock = styled(Box)`
  min-width: 0;
  > * + * {
    margin-top: 2px;
  }
`;

const FooterWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[1]}px;
  margin-top: auto;
`;

/* --- Progress + Milestones --- */

/* Relative container so markers can be absolutely positioned */
const MilestoneLayer = styled(Box)`
  position: relative;
  padding-top: 18px; /* space for icons above the bar */
`;

/* A single marker for a milestone; sits above the bar */
const MilestoneMarker = styled(Box)`
  position: absolute;
  left: ${({ $pos }) => $pos}%;
  top: 0; /* uses container's padding-top = space above bar */
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none; /* purely visual */
  z-index: 1;

  /* little stem down toward the bar */
  &:after {
    content: '';
    border-left: 1px dotted ${({ $stemColor }) => $stemColor};
    height: 8px;
    margin-top: 2px;
    border-radius: 2px;
  }
`;

const MetaRow = styled(Flex)`
  justify-content: space-between;
  align-items: center;
`;

const getColor = (theme, colorDefinition) => {
  const color = colorDefinition.split('.');
  const getColorRecursive = (obj, path) => {
    const [head, ...tail] = path;
    return tail.length > 0 ? getColorRecursive(obj[head], tail) : obj[head];
  };
  return getColorRecursive(theme.colors, color);
};

/* ----------------- component ----------------- */

const ProductProgressCard = ({
  name,
  subjectName,
  totalDuration,
  percentComplete,
  productType,
  thumbnailUrl,
  hasProduct,
  encouragementType,
  milestones,
  hideMilestone,
  showEncouragementText,
  description,
  ...restProps
}) => {
  const theme = useTheme();
  const safePercent = useMemo(() => clamp(percentComplete), [percentComplete]);
  const percentLabel = useMemo(() => `${Math.floor(safePercent ?? 0)}% COMPLETED`, [safePercent]);
  const encouragementText = useMemo(
    () => mapCompletedMilestonesToEncouragementText(encouragementType, milestones),
    [encouragementType, milestones]
  );

  return (
    <CardWrapper
      role="group"
      $subjectName={subjectName}
      aria-label={`${name} progress card`}
      {...restProps}
    >
      <HeaderRow>
        <LeftGroup>
          <Thumbnail>
            <Image
              src={thumbnailUrl}
              alt={subjectName}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getFallbackImage();
              }}
            />
          </Thumbnail>

          <TitleBlock>
            <Text
              textStyle="body1"
              fontWeight="500"
              color="myPath.textColor.primary"
              title={subjectName}
            >
              {subjectName}
              {description && (
                <Text as="span" textStyle="body1" color={description.color} marginLeft="4px">
                  {description.text}
                </Text>
              )}
            </Text>
            <Text textStyle="body2" color="myPath.textColor.primary" title={name} noWrap>
              {name}
            </Text>
            <Text textStyle="body2" color="myPath.textColor.secondary">
              {formatDuration(totalDuration)}
            </Text>
          </TitleBlock>
        </LeftGroup>
        <Box minWidth="100px">
          <ProductChip type={productType} marginLeft="auto" />
        </Box>
      </HeaderRow>

      {/* Progress with milestone markers */}
      <FooterWrapper>
        {hasProduct && (
          <MilestoneLayer>
            {/* Milestone icons (above the bar) */}
            {MILESTONES.map((m, idx) => {
              const reached = safePercent >= m.pos;
              const icon = reached ? m.afterIcon : m.beforeIcon;
              const iconOffset = reached ? m.afterIconOffset : m.beforeIconOffset;
              let iconColor = reached
                ? getColor(theme, m.afterIconColor)
                : getColor(theme, m.beforeIconColor);
              if (idx > 0 && safePercent < MILESTONES[idx - 1].pos) {
                iconColor = getColor(theme, 'myPath.textColor.disabled');
              }

              if (hideMilestone) {
                return null;
              }

              return (
                <MilestoneMarker
                  key={`${m}-${idx}`}
                  $pos={m.pos}
                  $stemColor={iconColor}
                  aria-label={`milestone ${m}% ${reached ? 'reached' : 'upcoming'}`}
                >
                  <SvgIcon icon={icon} size="16" color={iconColor} marginLeft={iconOffset} />
                </MilestoneMarker>
              );
            })}
            {/* Human Walk Icon */}
            <MilestoneMarker
              $pos={safePercent}
              $stemColor={getColor(theme, 'myPath.primary.300')}
              aria-label={`milestone ${safePercent}% reached`}
            >
              <SvgIcon
                icon={ICON_LIST.WALK}
                size="16"
                color={getColor(theme, 'myPath.primary.900')}
              />
            </MilestoneMarker>

            {/* The actual progress bar */}
            <ProgressBar
              milestones={MILESTONES}
              currentPercent={safePercent}
              value={safePercent}
              aria-label="progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={safePercent}
              aria-describedby={`progress-note-${subjectName}`}
              hideMilestone={hideMilestone}
            />
          </MilestoneLayer>
        )}

        <MetaRow>
          <Text textStyle="caption" color="myPath.textColor.secondary">
            {hasProduct ? percentLabel : ''}
          </Text>

          {showEncouragementText && (
            <Flex id={`progress-note-${subjectName}`} alignItems="center" gridColumnGap="4px">
              {hasProduct ? (
                safePercent === 0 ? (
                  <SvgIcon
                    icon={ICON_LIST.INFORMATION}
                    size="16"
                    color="myPath.textColor.secondary"
                  />
                ) : null
              ) : (
                <SvgIcon icon={ICON_LIST.INFORMATION} size="16" color="myPath.error.500" />
              )}
              <Text
                textStyle="caption"
                color={hasProduct ? 'myPath.textColor.secondary' : 'myPath.error.500'}
              >
                {hasProduct
                  ? safePercent === 0
                    ? 'ยังไม่เริ่มเรียน'
                    : encouragementText
                  : 'ยังไม่ได้ซื้อคอร์สเรียนนี้'}
              </Text>
            </Flex>
          )}
        </MetaRow>
      </FooterWrapper>
    </CardWrapper>
  );
};

ProductProgressCard.propTypes = {
  name: PropTypes.string.isRequired,
  subjectName: PropTypes.string.isRequired,
  totalDuration: PropTypes.number.isRequired, // seconds
  percentComplete: PropTypes.number,
  productType: PropTypes.oneOf(Object.keys(PRODUCT_TYPE)).isRequired,
  thumbnailUrl: PropTypes.string,
  hasProduct: PropTypes.bool,
  encouragementType: PropTypes.oneOf(Object.keys(ENCOURAGEMENT_TYPE)),
  milestones: PropTypes.arrayOf(
    PropTypes.shape({
      percent: PropTypes.number.isRequired,
      date: PropTypes.object.isRequired,
    })
  ),
  hideMilestone: PropTypes.bool,
  description: PropTypes.shape({
    text: PropTypes.string,
    color: PropTypes.string,
  }),
  showEncouragementText: PropTypes.bool,
};

ProductProgressCard.defaultProps = {
  percentComplete: 0,
  thumbnailUrl: null,
  hasProduct: false,
  encouragementType: ENCOURAGEMENT_TYPE['NO_MILESTONE'],
  milestones: [],
  hideMilestone: false,
  showEncouragementText: true,
};

export default ProductProgressCard;
